import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Heart, Focus, LayoutList, SlidersHorizontal, Check, Settings2 } from 'lucide-react';
import type { HomeVariant } from './blocks/types';

interface Props {
  variant: HomeVariant;
  onChange: (next: HomeVariant) => void;
  onCustomize?: () => void;
}

const META: Record<HomeVariant, { label: string; description: string; icon: typeof Heart }> = {
  cozy:    { label: 'Cozy',    description: 'The full experience — colour, puzzle, news.', icon: Heart },
  focused: { label: 'Focused', description: 'Lean — vital signs and apps only.',           icon: Focus },
  classic: { label: 'Classic', description: 'Original launcher view, untouched.',          icon: LayoutList },
  custom:  { label: 'Custom',  description: 'Your own layout — drag, hide, reorder.',      icon: SlidersHorizontal },
};

export const VariantSwitcher = ({ variant, onChange, onCustomize }: Props) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const ActiveIcon = META[variant].icon;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex h-9 items-center gap-2 rounded-md border border-line bg-bg-primary px-3 text-xs font-medium text-text-secondary transition-colors hover:border-brand-navy/40 hover:text-text-primary"
      >
        <ActiveIcon className="h-3.5 w-3.5" aria-hidden="true" />
        <span>{META[variant].label}</span>
        <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1.5 w-64 overflow-hidden rounded-lg border border-line bg-bg-primary shadow-lg"
        >
          {(Object.keys(META) as HomeVariant[]).map((v) => {
            const Icon = META[v].icon;
            const active = v === variant;
            return (
              <button
                key={v}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => {
                  onChange(v);
                  setOpen(false);
                }}
                className={[
                  'flex w-full items-start gap-3 px-3 py-2.5 text-left text-sm transition-colors',
                  active
                    ? 'bg-brand-navy/[0.06] text-text-primary'
                    : 'text-text-secondary hover:bg-bg-secondary hover:text-text-primary',
                ].join(' ')}
              >
                <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{META[v].label}</span>
                  <span className="block text-xs text-text-tertiary">{META[v].description}</span>
                </span>
                {active && <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-orange" />}
              </button>
            );
          })}
          {variant === 'custom' && onCustomize && (
            <button
              type="button"
              onClick={() => {
                onCustomize();
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 border-t border-line bg-bg-secondary px-3 py-2.5 text-left text-xs font-medium text-brand-navy hover:bg-brand-navy/[0.08]"
            >
              <Settings2 className="h-3.5 w-3.5" />
              Customize layout…
            </button>
          )}
        </div>
      )}
    </div>
  );
};
